/**
 * Creation and edition of a made-to-measure fabric with its picture.
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DMS_PRODUCTLINE, DMS_PRODUCTLINES } from '@/shared/constants';
import { ApiError } from '@/shared/api/client';
import { Dialog } from '@/shared/ui/Dialog';
import { PictureField } from '@/shared/ui/PictureField';
import { usePicture } from '@/shared/ui/use-picture';
import { createDmsItem, updateDmsItem, syncDmsItemPicture } from '../api';
import { fabricBooksQueryOptions } from '../fabric-books/api';
import {
  fabricDataSchema,
  fabricFormValues,
  fabricPictureUrl,
  formToFabricData,
  validateFabricForm,
  type DmsFabric,
  type FabricFormErrors,
  type FabricFormValues,
} from './fabric-form';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface FabricDialogProps {
  item: DmsFabric | null;
  /** Product line filter of the list, preset on a new fabric. */
  productLine: string | null;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const apiMessage = (error: unknown, fallback: string): string =>
  error instanceof ApiError && typeof error.body === 'string' && error.body
    ? error.body
    : fallback;

type TextKey = keyof Pick<
  FabricFormValues,
  | 'code'
  | 'description'
  | 'order'
  | 'titrage'
  | 'weight'
  | 'composition'
  | 'armure'
  | 'pattern'
  | 'colorGroup'
  | 'resupplyDate'
>;

export function FabricDialog({
  item,
  productLine,
  onClose,
}: Readonly<FabricDialogProps>) {
  const creating = !item;
  const [values, setValues] = useState<FabricFormValues>(() =>
    fabricFormValues(item, productLine),
  );
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FabricFormErrors>({});
  const [dirty, setDirty] = useState<Set<keyof FabricFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const picture = usePicture();
  const books = useQuery(fabricBooksQueryOptions());

  const set = <K extends keyof FabricFormValues>(
    key: K,
    value: FabricFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
  };
  const liveErrors = validateFabricForm(values);
  const invalidClass = (key: keyof FabricFormValues) =>
    dirty.has(key) && liveErrors[key] ? 'invalid' : '';
  const fieldError = (key: keyof FabricFormValues) => submitted && errors[key];
  const isShirt = values.productLine === DMS_PRODUCTLINE.SHIRT;

  const handlePicture = (id: string, code: string): Promise<string | null> =>
    picture.touched
      ? syncDmsItemPicture(id, 'main', code, picture.file)
      : Promise.resolve(item?.data.picture ?? null);

  const save = useMutation({
    mutationFn: async () => {
      const data = formToFabricData(values, Date.now());
      if (creating) {
        let created: DmsFabric;
        try {
          created = await createDmsItem(
            { type: 'fabric', data },
            fabricDataSchema,
          );
        } catch (error) {
          throw new Error(apiMessage(error, 'Failed to create item'));
        }
        try {
          const url = await handlePicture(
            created.id,
            String(created.data?.code ?? ''),
          );
          await updateDmsItem(
            created.id,
            {
              data: { ...created.data, picture: url },
            },
            fabricDataSchema,
          );
        } catch (error) {
          throw new Error('Failed to upload pictures', { cause: error });
        }
        return;
      }
      let url: string | null;
      try {
        url = await handlePicture(item.id, values.code);
      } catch (error) {
        throw new Error('Failed to upload pictures', { cause: error });
      }
      try {
        await updateDmsItem(
          item.id,
          {
            type: 'fabric',
            data: { ...data, picture: url },
          },
          fabricDataSchema,
        );
      } catch (error) {
        throw new Error(apiMessage(error, 'Failed to update item'));
      }
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateFabricForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const testId = (key: string) => `mtm-fabric-form-${kebabCase(key)}`;

  const text = (
    key: TextKey,
    label: string,
    message: string,
    inputType: 'text' | 'number' = 'text',
  ) => (
    <div className="mb-2">
      <label className="field-label" htmlFor={`fabric-${key}`}>
        {label}
      </label>
      <input
        id={`fabric-${key}`}
        data-testid={testId(key)}
        className={`field ${invalidClass(key)}`}
        name={key}
        type={inputType}
        placeholder={label}
        value={values[key]}
        onChange={(event) => set(key, event.target.value)}
      />
      <small hidden={!fieldError(key)} data-testid={`${testId(key)}-error`}>
        {message}
      </small>
    </div>
  );

  const checkbox = (
    key:
      'eveningOffer' | 'availablePermanent' | 'availableVillaDior' | 'lowStock',
    label: string,
  ) => (
    <div className="styled-input-square styled-input-single">
      <input
        type="checkbox"
        id={`fabric-${key}`}
        name={key}
        data-testid={testId(key)}
        checked={values[key]}
        onChange={(event) => set(key, event.target.checked)}
      />
      <label htmlFor={`fabric-${key}`}>{label}</label>
    </div>
  );

  return (
    <Dialog
      title={creating ? 'Add a fabric' : 'Edit a fabric'}
      width="800px"
      onClose={() => onClose('Cancel')}
      testId="mtm-fabric-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-4">
            <div className="mb-2">
              <label className="field-label" htmlFor="fabric-productLine">
                Product Line
              </label>
              <div
                className={`field field-selector ${invalidClass('productLine')}`}
              >
                <Select
                  id="fabric-productLine"
                  name="productLine"
                  testId={testId('productLine')}
                  placeholder="--Select product line--"
                  options={DMS_PRODUCTLINES}
                  value={values.productLine}
                  onChange={(line) => set('productLine', line)}
                />
              </div>
              <small
                hidden={!fieldError('productLine')}
                data-testid={`${testId('productLine')}-error`}
              >
                Wrong product line
              </small>
            </div>
            <div className="mb-2">
              <label className="field-label" htmlFor="fabric-book">
                Fabric Book
              </label>
              <div className={`field field-selector ${invalidClass('book')}`}>
                <Select
                  id="fabric-book"
                  name="book"
                  testId={testId('book')}
                  placeholder="--Select fabric book--"
                  options={optionsOf(
                    (books.data ?? []).map((book) => book.name),
                  )}
                  value={values.book}
                  onChange={(book) => set('book', book)}
                />
              </div>
              <small
                hidden={!fieldError('book')}
                data-testid={`${testId('book')}-error`}
              >
                Wrong fabric book
              </small>
            </div>
            {text('code', 'Fabric Code', 'Wrong fabric code')}
            {text('description', 'Description', 'Wrong description')}
            {text('order', 'Order', 'Wrong order', 'number')}
            {isShirt
              ? text('titrage', 'Titrage', 'Wrong titrage')
              : text('weight', 'Weight', 'Wrong weight')}
          </div>
          <div className="col-span-4">
            {text('composition', 'Composition', 'Wrong composition')}
            {text('armure', 'Armure', 'Wrong armure')}
            {text('pattern', 'Pattern', 'Wrong pattern')}
            {text('colorGroup', 'Color Group', 'Wrong color group')}
            {text('resupplyDate', 'Resupply Date', 'Wrong resupply date')}
            <div className="mb-2 mt-4 pt-6">
              {checkbox('eveningOffer', 'Evening Offer')}
            </div>
          </div>
          <div className="col-span-4">
            <PictureField
              id="fabric-picture"
              name="mainPicture"
              testId={testId('picture')}
              label="Picture"
              picture={picture}
              serverUrl={fabricPictureUrl(item)}
            />
            <div className="mb-1 mt-4">
              <span className="field-label">Availability</span>
            </div>
            {checkbox('availablePermanent', 'Permanent')}
            {checkbox('availableVillaDior', 'Villa Dior')}
            {checkbox('lowStock', 'Low Stock')}
          </div>
        </div>
        {errorMessage && (
          <div
            className="alert mb-0"
            role="alert"
            data-testid="mtm-fabric-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-6 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            data-testid="mtm-fabric-form-save-button"
            disabled={save.isPending}
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
